import { create } from 'zustand'
import type { MessageContext } from '../../api/types'

/** A voice message recorded and not sent yet. */
export interface Recording {
  blob: Blob
  /** As it's sent: "audio/mp4" or "audio/webm", say. */
  contentType: string
  /** "Voice message 14.32.05.m4a", from when it was recorded. */
  fileName: string
  durationMs: number
  /** Up to 64 levels of 0–100; empty when the microphone's levels couldn't be measured. */
  waveform: number[]
  /** It stopped by itself at 5 minutes. */
  limitReached: boolean
}

export type VoiceMessage =
  /** Waiting for the microphone. */
  | { phase: 'starting' }
  | { phase: 'recording'; startedAt: number; level: number }
  | {
      phase: 'recorded'
      recording: Recording
      /** ready: to listen back to, then send or delete. */
      status: 'ready' | 'sending' | 'failed'
      progress: number
      error: string | null
      /** What it answers, taken from the message box when Send is first tapped; null until then. */
      reply: VoiceReply | null
    }

/** The message a voice message answers, or the channel message of a private reply. */
export interface VoiceReply {
  replyToId: string | null
  context: MessageContext | null
}

interface VoiceMessagesState {
  /** The voice message being recorded, listened to or sent in each conversation. */
  byChannel: Record<string, VoiceMessage>
  set: (channelId: string, message: VoiceMessage | null) => void
}

/**
 * Voice messages by conversation, kept while the app is open: one recorded and not sent is still there after going to
 * another conversation and back, and one being sent carries on. The recorder itself loads with the first one.
 */
export const useVoiceMessages = create<VoiceMessagesState>((set, get) => ({
  byChannel: {},
  set: (channelId, message) => {
    const { [channelId]: _previous, ...others } = get().byChannel
    set({ byChannel: message ? { ...others, [channelId]: message } : others })
  },
}))
